import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Briefcase, MapPin, Calendar, Mail, Plus, Building2, Handshake, Clock, PoundSterling, Search } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import QuickActions from '@/components/QuickActions';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';

const BACKEND_URL = '';

const CONTRACT_OPTIONS = [
  { value: 'full_time',  label: 'Full Time'   },
  { value: 'part_time',  label: 'Part Time'   },
  { value: 'fixed_term', label: 'Fixed Term'  },
  { value: 'contract',   label: 'Contract'    },
  { value: 'internship', label: 'Internship'  },
];

const emptyForm = {
  title: '', location: '', contact_email: '', about: '', start_date: '',
  contract_type: 'full_time', salary: '', description: '',
  company_id: '', sponsor_id: '', closing_date: '',
};

export default function JobsPage({ user: userProp }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(userProp || null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterContract, setFilterContract] = useState('all');

  const [showSubmit, setShowSubmit] = useState(false);
  const [submitForm, setSubmitForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Company / partner dropdowns — fetched once for the submit form so
  // members can link their post to an existing tile rather than typing
  // a company name as plain text.
  const [companies, setCompanies] = useState([]);
  const [partners, setPartners] = useState([]);

  const [activeJob, setActiveJob] = useState(null);

  useEffect(() => {
    if (userProp) setUser(userProp);
    else {
      fetch(`${BACKEND_URL}/api/auth/session`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(u => u && setUser(u))
        .catch(() => {});
    }
  }, [userProp]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/jobs`, { credentials: 'include' });
      if (r.ok) setJobs(await r.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Companies + partners — used by the "About the company" picker.
    // Lightweight: a single bulk fetch on first form open is plenty.
    if (!showSubmit) return;
    fetch(`${BACKEND_URL}/api/companies`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(list => setCompanies(Array.isArray(list) ? list : (list.items || [])))
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/partners`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(list => setPartners(Array.isArray(list) ? list : (list.items || [])))
      .catch(() => {});
  }, [showSubmit]);

  const submit = async () => {
    if (!submitForm.title || !submitForm.contact_email || !submitForm.description) {
      toast.error('Title, contact email and description are required'); return;
    }
    setSubmitting(true);
    try {
      const body = { ...submitForm };
      // Don't send empty strings as ids — the model expects null/undefined.
      if (!body.company_id) delete body.company_id;
      if (!body.sponsor_id) delete body.sponsor_id;
      if (!body.closing_date) delete body.closing_date;
      const res = await fetch(`${BACKEND_URL}/api/jobs`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Submission failed'); }
      toast.success('Job submitted! Our team will review and publish shortly.');
      setShowSubmit(false);
      setSubmitForm(emptyForm);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const filtered = jobs.filter(j => {
    if (filterContract !== 'all' && j.contract_type !== filterContract) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (j.title || '').toLowerCase().includes(q) ||
      (j.location || '').toLowerCase().includes(q) ||
      (j.company_name || '').toLowerCase().includes(q) ||
      (j.sponsor_name || '').toLowerCase().includes(q)
    );
  });

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="container mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8">
        <SponsorBanner placement="jobs" type="top" className="mb-6 flex justify-center" />
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-brand-navy flex items-center gap-2">
              <Briefcase className="h-6 w-6 md:h-7 md:w-7" /> Jobs
            </h1>
            <p className="text-sm text-slate-500 mt-1">Roles from across the SLL community. Submitted by members, approved by our team.</p>
          </div>
          <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
            <DialogTrigger asChild>
              <Button data-testid="submit-job-btn" className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                <Plus className="h-4 w-4 mr-1.5" /> Post a Job
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submit-job-dialog">
              <DialogHeader>
                <DialogTitle>Post a Job</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <p className="text-xs text-slate-500">
                  Submissions are reviewed by the SLL team before going live. We'll email you once it's approved.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Job Title *</Label>
                    <Input value={submitForm.title} onChange={e => setSubmitForm(f => ({ ...f, title: e.target.value }))} placeholder="Head of Sustainability" data-testid="job-title-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Location *</Label>
                    <Input value={submitForm.location} onChange={e => setSubmitForm(f => ({ ...f, location: e.target.value }))} placeholder="London / Remote" data-testid="job-location-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Application Email *</Label>
                    <Input type="email" value={submitForm.contact_email} onChange={e => setSubmitForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="careers@company.com" data-testid="job-contact-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Salary</Label>
                    <Input value={submitForm.salary} onChange={e => setSubmitForm(f => ({ ...f, salary: e.target.value }))} placeholder="£45-55k / Competitive" data-testid="job-salary" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Contract Type</Label>
                    <Select value={submitForm.contract_type} onValueChange={v => setSubmitForm(f => ({ ...f, contract_type: v }))}>
                      <SelectTrigger data-testid="job-contract-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONTRACT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input value={submitForm.start_date} onChange={e => setSubmitForm(f => ({ ...f, start_date: e.target.value }))} placeholder="ASAP / 1 April 2026" data-testid="job-start-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Closing Date</Label>
                    <Input type="date" value={submitForm.closing_date} onChange={e => setSubmitForm(f => ({ ...f, closing_date: e.target.value }))} data-testid="job-closing-date" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">About the Role</Label>
                  <Textarea rows={2} value={submitForm.about} onChange={e => setSubmitForm(f => ({ ...f, about: e.target.value }))} placeholder="One-line summary that appears under the title." data-testid="job-about" />
                </div>
                <div>
                  <Label className="text-xs">Job Description *</Label>
                  <Textarea rows={6} value={submitForm.description} onChange={e => setSubmitForm(f => ({ ...f, description: e.target.value }))} placeholder="Full description: responsibilities, requirements, what success looks like..." data-testid="job-description" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Link to Company</Label>
                    <Select value={submitForm.company_id || 'none'} onValueChange={v => setSubmitForm(f => ({ ...f, company_id: v === 'none' ? '' : v, sponsor_id: v !== 'none' ? '' : f.sponsor_id }))}>
                      <SelectTrigger data-testid="job-company-select"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">…or Partner Profile</Label>
                    <Select value={submitForm.sponsor_id || 'none'} onValueChange={v => setSubmitForm(f => ({ ...f, sponsor_id: v === 'none' ? '' : v, company_id: v !== 'none' ? '' : f.company_id }))}>
                      <SelectTrigger data-testid="job-partner-select"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {partners.map(p => <SelectItem key={p.sponsor_id} value={p.sponsor_id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowSubmit(false)}>Cancel</Button>
                  <Button onClick={submit} disabled={submitting} data-testid="submit-job-confirm" className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                    {submitting ? 'Submitting…' : 'Submit for review'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search + filter */}
        <div className="flex flex-wrap gap-2 mb-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input className="pl-9" placeholder="Search by title, location, company…" value={search} onChange={e => setSearch(e.target.value)} data-testid="jobs-search" />
          </div>
          <Select value={filterContract} onValueChange={setFilterContract}>
            <SelectTrigger className="w-44" data-testid="jobs-contract-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All contract types</SelectItem>
              {CONTRACT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-sm text-slate-400 py-10 text-center">Loading…</p>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-3">{jobs.length === 0 ? 'No jobs posted yet. Be the first!' : 'No jobs match your filters.'}</p>
            {jobs.length === 0 && (
              <Button onClick={() => setShowSubmit(true)} variant="outline">Post the first job</Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="jobs-list">
            {filtered.map(job => (
              <Card key={job.job_id} className="p-5 border border-slate-200 hover:shadow-md transition-all flex flex-col" data-testid={`job-card-${job.job_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-800 truncate">{job.title}</h3>
                    {job.about && <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{job.about}</p>}
                  </div>
                  <Badge variant="secondary" className="text-[10px] flex-shrink-0">{job.contract_type_label}</Badge>
                </div>
                <div className="space-y-1 text-xs text-slate-500 mb-3">
                  {job.location && <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {job.location}</div>}
                  {job.salary && <div className="flex items-center gap-1.5"><PoundSterling className="h-3 w-3" /> {job.salary}</div>}
                  {job.start_date && <div className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> Starts {job.start_date}</div>}
                  <div className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Posted {fmtDate(job.posted_at)}</div>
                  {(job.company_name || job.sponsor_name) && (
                    <button
                      onClick={() => job.company_id ? navigate(`/companies/${job.company_id}`) : navigate(`/partners/${job.sponsor_id}`)}
                      className="inline-flex items-center gap-1 text-brand-navy hover:underline mt-1"
                      data-testid={`job-company-link-${job.job_id}`}
                    >
                      {job.sponsor_id ? <Handshake className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                      {job.company_name || job.sponsor_name}
                    </button>
                  )}
                </div>
                <div className="flex gap-2 mt-auto pt-2">
                  <Button size="sm" variant="outline" onClick={() => setActiveJob(job)} data-testid={`job-view-${job.job_id}`} className="flex-1">View details</Button>
                  <a
                    href={`mailto:${job.contact_email}?subject=${encodeURIComponent(`Application for ${job.title}`)}&body=${encodeURIComponent(`Hi,\n\nI'd like to apply for the ${job.title} role I saw on SLL BriteHub.\n\nThanks,`)}`}
                    data-testid={`job-apply-${job.job_id}`}
                  >
                    <Button size="sm" className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                      <Mail className="h-4 w-4 mr-1.5" /> Apply
                    </Button>
                  </a>
                </div>
              </Card>
            ))}
          </div>
        )}

        <QuickActions user={user} exclude={['/jobs']} />
          </div>
          <PageSideRail placement="jobs" />
        </div>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!activeJob} onOpenChange={(v) => { if (!v) setActiveJob(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="job-detail-dialog">
          {activeJob && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg md:text-xl pr-8 break-words">{activeJob.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-1">
                <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                  {activeJob.location && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100"><MapPin className="h-3 w-3" /> {activeJob.location}</span>}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100"><Briefcase className="h-3 w-3" /> {activeJob.contract_type_label}</span>
                  {activeJob.salary && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100"><PoundSterling className="h-3 w-3" /> {activeJob.salary}</span>}
                  {activeJob.start_date && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100"><Clock className="h-3 w-3" /> Starts {activeJob.start_date}</span>}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100"><Calendar className="h-3 w-3" /> Posted {fmtDate(activeJob.posted_at)}</span>
                  {activeJob.closing_date && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Closes {fmtDate(activeJob.closing_date)}</span>}
                </div>
                {activeJob.about && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">About the role</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{activeJob.about}</p>
                  </div>
                )}
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Job description</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{activeJob.description}</p>
                </div>
                {(activeJob.company_name || activeJob.sponsor_name) && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">About the company</p>
                    <button
                      onClick={() => activeJob.company_id ? navigate(`/companies/${activeJob.company_id}`) : navigate(`/partners/${activeJob.sponsor_id}`)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-brand-navy/5 text-brand-navy hover:bg-brand-navy/10 font-medium text-sm"
                    >
                      {activeJob.sponsor_id ? <Handshake className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                      View {activeJob.company_name || activeJob.sponsor_name}'s profile
                    </button>
                  </div>
                )}
                <div className="pt-3 border-t flex justify-end">
                  <a
                    href={`mailto:${activeJob.contact_email}?subject=${encodeURIComponent(`Application for ${activeJob.title}`)}&body=${encodeURIComponent(`Hi,\n\nI'd like to apply for the ${activeJob.title} role I saw on SLL BriteHub.\n\nThanks,`)}`}
                  >
                    <Button className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                      <Mail className="h-4 w-4 mr-1.5" /> Apply now
                    </Button>
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
